import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Box, CssBaseline, Container, useMediaQuery, useTheme, Drawer } from '@mui/material';
// استيراد المكونات بشكل مباشر بدلاً من استيرادها من index.ts لتجنب الاستيراد الدائري
import StoreHeader from './StoreHeader';
import StoreFooter from './StoreFooter';
import ThemeProvider from './ThemeProvider';
import MobileMegaMenu from './MobileMegaMenu';
import { useStoreSettings } from '../../contexts/StoreSettingsContext';
import { updateFavicon } from '../../utils/faviconUtils';
import ScrollToTopButton from '../common/ScrollToTopButton';

const StoreLayout: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | undefined>(undefined);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { settings } = useStoreSettings();
  const navigate = useNavigate();
  const location = useLocation();

  // تعيين عنوان الصفحة والأيقونة بناءً على إعدادات المتجر
  useEffect(() => {
    if (settings?.general?.storeName) {
      // استخدام اسم الشركة في عنوان المتصفح
      document.title = settings.general.storeName;

      // استخدام أيقونة افتراضية
      const faviconUrl = '/favicon.ico';

      // إضافة أيقونة المتجر
      const existingFavicon = document.querySelector('link[rel="icon"]');
      if (existingFavicon) {
        // إذا كان هناك أيقونة موجودة، قم بتحديثها
        // نحاول استخدام ملف الأيقونة أولاً، وإذا فشل نستخدم الأيقونة الديناميكية
        try {
          const faviconImg = new Image();
          faviconImg.src = `${process.env.PUBLIC_URL}/favicon.ico?v=${Date.now()}`;
          faviconImg.onerror = () => {
            existingFavicon.setAttribute('href', faviconUrl);
          };
          faviconImg.onload = () => {
            existingFavicon.setAttribute('href', `${process.env.PUBLIC_URL}/favicon.ico?v=${Date.now()}`);
          };
        } catch (error) {
          existingFavicon.setAttribute('href', faviconUrl);
        }
      } else {
        // إذا لم تكن هناك أيقونة، قم بإنشاء واحدة جديدة
        const favicon = document.createElement('link');
        favicon.rel = 'icon';
        favicon.href = faviconUrl;
        document.head.appendChild(favicon);
      }

      // إضافة أيقونة shortcut icon إذا لم تكن موجودة
      if (!document.querySelector('link[rel="shortcut icon"]')) {
        const shortcutIcon = document.createElement('link');
        shortcutIcon.rel = 'shortcut icon';
        shortcutIcon.href = faviconUrl;
        document.head.appendChild(shortcutIcon);
      }
    }

    // إعادة تحميل الأيقونة كل 5 ثوانٍ
    const intervalId = setInterval(() => {
      const icons = document.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"]');
      icons.forEach(icon => {
        const href = icon.getAttribute('href');
        if (href) {
          const newHref = href.split('?')[0] + `?v=${Date.now()}`;
          icon.setAttribute('href', newHref);
        }
      });
    }, 5000);

    // تنظيف الفاصل الزمني عند إلغاء تحميل المكون
    return () => clearInterval(intervalId);
  }, [settings]);

  // تحديث الأيقونة عند تغيير الصفحة
  useEffect(() => {
    // تحديث جميع الأيقونات في كل مرة يتغير فيها المسار
    const icons = document.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"]');
    icons.forEach(icon => {
      const href = icon.getAttribute('href');
      if (href) {
        // إضافة معلمة عشوائية لمنع التخزين المؤقت
        const newHref = href.split('?')[0] + `?v=${Date.now()}`;
        icon.setAttribute('href', newHref);
      }
    });
  }, [location.pathname]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    // يمكن إضافة منطق إضافي هنا للبحث
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleCategorySelect = (categoryId: string) => {
    setSelectedCategoryId(categoryId);
    navigate(`/category/${categoryId}`);
    setMobileMenuOpen(false);
  };

  return (
    <ThemeProvider>
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <CssBaseline />

        {/* رأس المتجر */}
        <StoreHeader
          onSearch={handleSearch}
          onMobileMenuOpen={handleMobileMenuToggle}
        />

        {/* قائمة الجوال */}
        <Drawer
          anchor="right"
          open={mobileMenuOpen}
          onClose={handleMobileMenuToggle}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: '85%',
              maxWidth: 320,
              boxShadow: '0 0 20px rgba(0,0,0,0.15)',
              borderTopLeftRadius: 8,
              borderBottomLeftRadius: 8
            },
          }}
        >
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            backgroundColor: '#f9f9f9'
          }}>
            <MobileMegaMenu
              onCategorySelect={handleCategorySelect}
              onClose={handleMobileMenuToggle}
            />
          </Box>
        </Drawer>

        {/* محتوى الصفحة - عرض كامل */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            width: '100%',
            px: { xs: 0, sm: 0 },
            py: 3
          }}
        >
          <Container
            maxWidth={false}
            disableGutters
            sx={{
              px: { xs: 1, sm: 2, md: 3 }
            }}
          >
            <Outlet context={{ searchQuery, selectedCategoryId }} />
          </Container>
        </Box>

        {/* تذييل المتجر */}
        <StoreFooter />

        {/* زر العودة للأعلى */}
        <ScrollToTopButton
          bottom={20}
          right={20}
          showAfter={300}
          size="medium"
          color="primary"
          tooltip="العودة للأعلى"
        />
      </Box>
    </ThemeProvider>
  );
};

export default StoreLayout;
